/**
 * Excel Utility Functions for Incident Facilities Reports
 * Handles Community Kitchen, Animal Shelter, and Flood Relief Camp
 */

/**
 * Format date to match Web UI format
 */
export const formatDate = (dateStr, includeTime = false) => {
  if (!dateStr || dateStr === 'NA' || dateStr === '' || dateStr === null || dateStr === undefined) {
    return 'NA';
  }

  if (typeof dateStr === 'string' && dateStr.includes('1111')) {
    return 'NA';
  }

  try {
    if (typeof dateStr === 'string' && dateStr.includes('/') && dateStr.includes(':')) {
      return dateStr;
    }

    if (typeof dateStr === 'string' && dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      return dateStr;
    }

    const date = new Date(dateStr);
    
    if (isNaN(date.getTime())) {
      return 'NA';
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    if (includeTime) {
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      const hoursStr = String(hours).padStart(2, '0');
      
      return `${day}/${month}/${year} ${hoursStr}:${minutes}:${seconds} ${ampm}`;
    } else {
      return `${day}/${month}/${year}`;
    }
  } catch (e) {
    console.warn('Date formatting error:', e, dateStr);
    return 'NA';
  }
};

/**
 * Get current timestamp in IST format for filename
 */
export const getTimestamp = () => {
  const now = new Date();
  const istOffset = 330;
  const istTime = new Date(now.getTime() + (istOffset * 60000));
  
  const year = istTime.getUTCFullYear();
  const month = String(istTime.getUTCMonth() + 1).padStart(2, '0');
  const day = String(istTime.getUTCDate()).padStart(2, '0');
  const hours = String(istTime.getUTCHours()).padStart(2, '0');
  const minutes = String(istTime.getUTCMinutes()).padStart(2, '0');
  const seconds = String(istTime.getUTCSeconds()).padStart(2, '0');
  
  return `${year}-${month}-${day}_${hours}${minutes}${seconds}`;
};

/**
 * Transform Community Kitchen data for Excel
 */
export const transformCommunityKitchenData = (rawData) => {
  console.log('🍳 Transforming Community Kitchen data');

  if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
    return { columns: [], transformedData: [] };
  }

  const fieldMapping = {
    'facility_name': 'Facility',
    'district_name': 'District',
    'block_name': 'Block',
    'created_on': 'Created on',  // lowercase 'o'
    'open_date': 'Open Date',
    'open_status': 'Status',
    'close_date': 'Close Date',
    'breakfast': 'Breakfast',
    'lunch': 'Lunch',
    'dinner': 'Dinner',
    'total_meals': 'Total Meals'
  };

  const excelColumnLabels = [
    'Facility',
    'District',
    'Block',
    'Created on',
    'Open Date',
    'Status',
    'Close Date',
    'Breakfast',
    'Lunch',
    'Dinner',
    'Total Meals'
  ];

  const columns = excelColumnLabels.map(label => ({ label, key: label }));

  const transformedData = rawData.map(row => {
    const transformedRow = {};

    Object.keys(fieldMapping).forEach(apiField => {
      const displayName = fieldMapping[apiField];
      const value = row[apiField];

      if (apiField === 'created_on') {
        transformedRow[displayName] = formatDate(value, true);  // with time
      } else if (apiField === 'open_date' || apiField === 'close_date') {
        transformedRow[displayName] = formatDate(value, false);  // without time
      } else if (apiField === 'open_status') {
        transformedRow[displayName] = value || 'NA';
      } else if (['breakfast', 'lunch', 'dinner', 'total_meals'].includes(apiField)) {
        transformedRow[displayName] = (value !== null && value !== undefined) ? value : 0;
      } else {
        transformedRow[displayName] = value || 'NA';
      }
    });

    return transformedRow;
  });

  console.log(`✅ Transformed ${transformedData.length} Community Kitchen records`);
  return { columns, transformedData };
};

/**
 * Transform Animal Shelter data for Excel
 */
export const transformAnimalShelterData = (rawData) => {
  console.log('🐾 Transforming Animal Shelter data');

  if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
    return { columns: [], transformedData: [] };
  }

  const fieldMapping = {
    'facility_name': 'Facility',
    'district_name': 'District',
    'block_name': 'Block',
    'created_on': 'Created On',  // ✅ Capital 'O' to match Web UI
    'open_status': 'Status',
    'open_date': 'Open Date',
    'close_date': 'Close Date',
    'operational_date': 'Operational Date',
    'total_animals_treated': 'Animals Treated',
    'total_animals_fed': 'Animals Fed',
    'total_fodder': 'Quantity of Fodder (in Kg)'
  };

  const excelColumnLabels = [
    'Facility',
    'District',
    'Block',
    'Created On',  // ✅ Capital 'O'
    'Status',
    'Open Date',
    'Close Date',
    'Operational Date',
    'Animals Treated',
    'Animals Fed',
    'Quantity of Fodder (in Kg)'
  ];

  const columns = excelColumnLabels.map(label => ({ label, key: label }));

const transformedData = rawData
  .sort((a, b) => {
    // Put rows with null/NA created_on at the TOP (not bottom)
    if (!a.created_on || a.created_on === 'NA' || a.created_on === null) return -1;
    if (!b.created_on || b.created_on === 'NA' || b.created_on === null) return 1;
    
    // Sort valid dates: newest first (descending)
    const dateA = new Date(a.created_on);
    const dateB = new Date(b.created_on);
    return dateB - dateA; // Descending: Dec 2025 → Oct 2024
  })
  .map(row => {
    const transformedRow = {};

    Object.keys(fieldMapping).forEach(apiField => {
      const displayName = fieldMapping[apiField];
      const value = row[apiField];

      if (apiField === 'created_on') {
        transformedRow[displayName] = formatDate(value, false);  // ✅ NO time for Animal Shelter
      } else if (['open_date', 'close_date', 'operational_date'].includes(apiField)) {
        transformedRow[displayName] = formatDate(value, false);
      } else if (apiField === 'open_status') {
        transformedRow[displayName] = value || 'NA';
      } else if (['total_animals_treated', 'total_animals_fed', 'total_fodder'].includes(apiField)) {
        transformedRow[displayName] = (value !== null && value !== undefined) ? value : 0;
      } else {
        transformedRow[displayName] = value || 'NA';
      }
    });

    return transformedRow;
  });

  console.log(`✅ Transformed ${transformedData.length} Animal Shelter records`);
  return { columns, transformedData };
};

/**
 * Transform Flood Relief Camp data for Excel
 */
export const transformFloodReliefCampData = (rawData) => {
  console.log('🏕️ Transforming Flood Relief Camp data');

  if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
    return { columns: [], transformedData: [] };
  }

  const fieldMapping = {
    'facility_name': 'Facility',
    'district_name': 'District',
    'block_name': 'Block',
    'created_on': 'Creatd On',  // ✅ TYPO in Web UI - must match exactly!
    'open_status': 'Status',
    'open_date': 'Open Date',
    'close_date': 'Close Date',
    'breakfast': 'Breakfast',
    'lunch': 'Lunch',
    'dinner': 'Dinner',
    'total_meals': 'Total Meals'
  };

  const excelColumnLabels = [
    'Facility',
    'District',
    'Block',
    'Creatd On',  // ✅ Keep typo to match Web UI
    'Status',
    'Open Date',
    'Close Date',
    'Breakfast',
    'Lunch',
    'Dinner',
    'Total Meals'
  ];

  const columns = excelColumnLabels.map(label => ({ label, key: label }));

  const transformedData = rawData.map(row => {
    const transformedRow = {};

    Object.keys(fieldMapping).forEach(apiField => {
      const displayName = fieldMapping[apiField];
      const value = row[apiField];

      if (apiField === 'created_on') {
        transformedRow[displayName] = formatDate(value, true);  // ✅ WITH time for Flood Relief
      } else if (['open_date', 'close_date'].includes(apiField)) {
        transformedRow[displayName] = formatDate(value, false);
      } else if (apiField === 'open_status') {
        transformedRow[displayName] = value || 'NA';
      } else if (['breakfast', 'lunch', 'dinner', 'total_meals'].includes(apiField)) {
        transformedRow[displayName] = (value !== null && value !== undefined) ? value : 0;
      } else {
        transformedRow[displayName] = value || 'NA';
      }
    });

    return transformedRow;
  });

  console.log(`✅ Transformed ${transformedData.length} Flood Relief Camp records`);
  return { columns, transformedData };
};
